import { connect } from "react-redux";

import { withResolvePaths } from "../../withResolvePaths";
import {
    FormActionHandlerExecutionInjectableProps,
    withFormActionHandlerExecution,
} from "../../Internals/FormActionHandlers/WithFormActionHandlerExecution";
import { withViewModelSelect } from "../../withViewModelSelect";
import { EvaluableProp, withEvaluators } from "../../withEvaluators";
import { withTestPageObjectRegistration } from "../../Services/TestPageObjects/PageObjectExposure";
import { withFocusManagement } from "../../Services/FocusManagement/withFocusManagement";
import { withExternalMountingEventReporting } from "../../Services/ExternalMountingEventReporting/withExternalMountingEventReporting";
import { withControlDebugInfo } from "../../withControlDebugInfo";
import { CandyDispatch } from "../../../Engine/Core/Store/CandyReduxTypes";
import { setToModel } from "../../../Engine/ActionCreators/AllActionCreators";
import { isNotNullOrUndefined } from "../../../../../Common/TypingUtils";
import { AbsoluteResolvedModelPath } from "../../../../../Common/ModelPath/ModelPath";
import { ViewModelSelect } from "../../../Helpers/StateAccessors/ViewModelSelect";
import { FullFormState } from "../../../Engine/Core/Store/FullFormState";
import { FormActionHandler } from "../../Internals/FormActionHandlers/FormActionHandler";

import { Toggle, ToggleValue } from "./Toggle";

interface ToggleContainerProps extends FormActionHandlerExecutionInjectableProps {
    bindingPath: AbsoluteResolvedModelPath;
    viewModelSelect: ViewModelSelect;
    disabled2?: EvaluableProp<boolean>;
    processEvaluableProp: <T>(state: FullFormState, prop: EvaluableProp<T>) => T;
    callHelperOnChange: FormActionHandler;
}

const mapStateToProps = (state: FullFormState, ownProps: ToggleContainerProps) => {
    const { bindingPath, viewModelSelect } = ownProps;
    const controlModel = viewModelSelect.controlModel(state, bindingPath);

    return {
        value: controlModel.value ?? false,
        error: controlModel.error,
        warning: controlModel.error ? undefined : controlModel.warning,
        disabled: ownProps.processEvaluableProp(state, ownProps.disabled2),
        isReadOnly: state.settings.isReadOnly,
    };
};

const mapDispatchToProps = (dispatch: CandyDispatch, ownProps: ToggleContainerProps) => {
    const { bindingPath } = ownProps;

    return {
        onChange: async (value: undefined | ToggleValue) => {
            dispatch(setToModel(bindingPath, "value", value));
            if (isNotNullOrUndefined(ownProps.callHelperOnChange)) {
                await ownProps.executeFormActionHandler(ownProps.callHelperOnChange);
            }
        },
        setCorrectValue: (value: undefined | ToggleValue) => dispatch(setToModel(bindingPath, "value", value)),
        setError: (error: boolean) => {
            dispatch(setToModel(bindingPath, "error", error ? [error] : []));
        },
    };
};

export const ToggleContainer = withResolvePaths(
    withFormActionHandlerExecution(
        withViewModelSelect(
            withEvaluators(
                connect(
                    mapStateToProps,
                    mapDispatchToProps
                )(
                    withTestPageObjectRegistration(
                        withFocusManagement(withExternalMountingEventReporting(withControlDebugInfo(Toggle)))
                    )
                )
            )
        )
    )
);
